/**
 * Tiny client-side store for the dashboard tracking surfaces
 * (Saved Jobs, Job Alerts, Applications). Uses localStorage so
 * the JobHacki backend stays keyless and these lists sync per-device.
 *
 * Each saved job key is the apply URL — it's the only thing that's
 * guaranteed unique across ATS sources.
 */

const KEYS = {
  saved: "jh_saved_v1",
  alerts: "jh_alerts_v1",
  apps: "jh_applications_v1",
  searches: "jh_saved_searches_v1",
  playbooks: "jh_playbooks_v1",
  recentPlaybooks: "jh_recent_playbooks",
};

function safeRead(k, fallback) {
  if (typeof window === "undefined") return fallback;
  try {
    const raw = window.localStorage.getItem(k);
    if (!raw) return fallback;
    const v = JSON.parse(raw);
    return v ?? fallback;
  } catch {
    return fallback;
  }
}

function safeWrite(k, v) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(k, JSON.stringify(v));
    window.dispatchEvent(new CustomEvent("jh:storage", { detail: { key: k } }));
  } catch {
    /* quota / private mode — fail silently */
  }
}

/* ───── Generic helpers ───── */
export function getList(kind) {
  return safeRead(KEYS[kind], []);
}

export function setList(kind, list) {
  safeWrite(KEYS[kind], list);
}

/* ───── Saved jobs ───── */
export function isSaved(job) {
  if (!job?.url) return false;
  return getList("saved").some((j) => j.url === job.url);
}

export function toggleSaved(job) {
  const list = getList("saved");
  const idx = list.findIndex((j) => j.url === job.url);
  if (idx >= 0) list.splice(idx, 1);
  else list.unshift({ ...job, savedAt: Date.now() });
  setList("saved", list);
  return idx < 0;
}

/* ───── Alerts (per-search) ───── */
export function hasAlert(job) {
  if (!job?.url) return false;
  return getList("alerts").some((j) => j.url === job.url);
}

export function toggleAlert(job) {
  const list = getList("alerts");
  const idx = list.findIndex((j) => j.url === job.url);
  if (idx >= 0) list.splice(idx, 1);
  else list.unshift({ ...job, alertAt: Date.now() });
  setList("alerts", list);
  return idx < 0;
}

/* ───── Applications ───── */
export const APP_STATUSES = [
  "applied",
  "screen",
  "interview",
  "offer",
  "rejected",
];

export function getApplication(job) {
  if (!job?.url) return null;
  return getList("apps").find((a) => a.url === job.url) || null;
}

export function logApplication(job, status = "applied") {
  const list = getList("apps");
  const idx = list.findIndex((a) => a.url === job.url);
  const next = {
    url: job.url,
    title: job.title,
    company: job.company,
    location: job.location,
    appliedAt: Date.now(),
    status,
  };
  if (idx >= 0) list[idx] = { ...list[idx], ...next };
  else list.unshift(next);
  setList("apps", list);
}

export function setApplicationStatus(url, status) {
  const list = getList("apps");
  const idx = list.findIndex((a) => a.url === url);
  if (idx < 0) return;
  list[idx] = { ...list[idx], status, updatedAt: Date.now() };
  setList("apps", list);
}

export function removeApplication(url) {
  const list = getList("apps").filter((a) => a.url !== url);
  setList("apps", list);
}

/* ───── Saved searches ───── */
export function getSavedSearches() {
  return getList("searches");
}

export function saveSearch(params, label) {
  const list = getList("searches");
  const next = { id: `s_${Date.now()}`, label, params, savedAt: Date.now() };
  list.unshift(next);
  setList("searches", list.slice(0, 30));
  return next;
}

export function deleteSavedSearch(id) {
  setList(
    "searches",
    getList("searches").filter((s) => s.id !== id)
  );
}

/* ───── Playbook bookmarks + recently-viewed ───── */
export function isPlaybookSaved(slug) {
  if (!slug) return false;
  return getList("playbooks").some((p) => p.slug === slug);
}

export function togglePlaybookSaved(pb) {
  const list = getList("playbooks");
  const idx = list.findIndex((p) => p.slug === pb.slug);
  if (idx >= 0) list.splice(idx, 1);
  else
    list.unshift({
      slug: pb.slug,
      title: pb.title,
      category: pb.category,
      url: pb.url,
      savedAt: Date.now(),
    });
  setList("playbooks", list);
  return idx < 0;
}

export function getRecentPlaybooks() {
  return safeRead(KEYS.recentPlaybooks, []);
}

export function markPlaybookViewed(slug) {
  if (!slug) return;
  const recent = getRecentPlaybooks().filter((s) => s !== slug);
  recent.unshift(slug);
  safeWrite(KEYS.recentPlaybooks, recent.slice(0, 30));
}

/* ───── Cross-device sync with /api/me/lists ─────
 * Contract: GET → {ok, saved, alerts, applications, playbooks}
 *           POST {saved, alerts, applications, playbooks} → full upsert
 * Server-wins on hydrate; localStorage-wins on offline. */
import { getMyLists, saveMyLists } from "@/lib/api";

function snapshot() {
  return {
    saved: getList("saved"),
    alerts: getList("alerts"),
    applications: getList("apps"),
    playbooks: getList("playbooks"),
  };
}

/** Pull server state and overwrite local copies. Silent on 401/network. */
export async function hydrateFromServer() {
  try {
    const d = await getMyLists();
    if (!d || d.ok === false) return false;
    if (Array.isArray(d.saved)) setList("saved", d.saved);
    if (Array.isArray(d.alerts)) setList("alerts", d.alerts);
    if (Array.isArray(d.applications)) setList("apps", d.applications);
    if (Array.isArray(d.playbooks)) setList("playbooks", d.playbooks);
    return true;
  } catch {
    return false;
  }
}

let syncTimer = null;
let syncing = false;

/** Debounced full-state POST. Coalesces bursts of edits into 1 call. */
export function syncToServerSoon(delayMs = 800) {
  if (typeof window === "undefined") return;
  if (syncTimer) clearTimeout(syncTimer);
  syncTimer = setTimeout(async () => {
    if (syncing) return;
    syncing = true;
    try {
      await saveMyLists(snapshot());
    } catch {
      /* swallow — local is the truth until we reconnect */
    } finally {
      syncing = false;
    }
  }, delayMs);
}

/** Auto-trigger a debounced sync whenever any tracked list changes. */
if (typeof window !== "undefined" && !window.__jhSyncWired) {
  window.__jhSyncWired = true;
  window.addEventListener("jh:storage", (e) => {
    const k = e?.detail?.key || "";
    if (
      k === KEYS.saved ||
      k === KEYS.alerts ||
      k === KEYS.apps ||
      k === KEYS.playbooks
    ) {
      syncToServerSoon();
    }
  });
}
